import { ApiResponse } from "../../types/common";
import { User } from "../../types/RoleType";
import { axiosInstance } from "./axios";
import type { OtpChannel, ResetAuthorization, ResetChallenge, ResetChannels } from "../../types/auth";

export interface EmployeeProfileUpdate {
    phoneNumber?: string;
    bloodGroup?: string;
    maritalStatus?: string;
    nationality?: string;
    religion?: string;
    emergencyContactNumber?: string;
    communicationAddress?: string;
    pincode?: string;
    healthMedicalRecords?: string;
    aadhaarNumber?: string;
    parentorSpouseName?: string;
    dateOfBirth?: string;
    gender?: string;
}

export interface StudentProfileUpdate {
    phoneNumber?: string;
    bloodGroup?: string;
    religion?: string;
    nationality?: string;
    caste?: string;
    communicationAddress?: string;
    pincode?: string;
    healthMedicalRecords?: string;
    aadhaarNumber?: string;
    fatherName?: string;
    fatherMobileNumber?: string;
    fatherOccupation?: string;
    motherName?: string;
    motherMobileNumber?: string;
    motherOccupation?: string;
    gender?: string;
    dateOfBirth?: string;
}

export const usersApi = {
    getUserById: async (id: string): Promise<{ user: User }> => {
        const response = await axiosInstance.get<ApiResponse<User>>(`v1/Users/${id}`);
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to fetch');
        }
        return { user: response.data.data }
    },
    updateUser: async (user: User): Promise<ApiResponse<boolean>> => {
        const response = await axiosInstance.put<ApiResponse<boolean>>(`v1/Users`, {
            userId: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            userName: user.userName,    
            profilePicture: user.profileImageUrl,
            phoneNumber: user.phoneNumber,
        },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        return response.data;
    },
    getuserProfile : async (id: string): Promise<{ user: User }> => {
        const response = await axiosInstance.get<ApiResponse<User>>(`v1/Users/profile/${id}`);
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to fetch');
        }
        return { user: response.data.data }
    },
    forgotPassword: async (
        userNameOrArgs: string | { userName: string; channel: OtpChannel }
    ): Promise<ApiResponse<boolean> | ResetChallenge> => {
        if (typeof userNameOrArgs === 'string') {
            const response = await axiosInstance.post<ApiResponse<boolean>>(`Account/ForgotPassword`, { userName: userNameOrArgs });
            if (!response.data.succeeded) {
                throw new Error(response.data.messages?.[0] || 'failed to send reset link');
            }
            return response.data;
        }
        const response = await axiosInstance.post<ApiResponse<ResetChallenge>>(`Account/ForgotPassword`, {
            userName: userNameOrArgs.userName,
            channel: userNameOrArgs.channel,
        });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to send code');
        }
        return { ...(response.data.data ?? {}), _message: response.data.messages?.[0] } as ResetChallenge & { _message?: string };
    },
    resolveResetChannels: async (userName: string): Promise<ResetChannels> => {
        const response = await axiosInstance.post<ApiResponse<ResetChannels>>(`Account/ResolveResetChannels`, { userName });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to resolve reset channels');
        }
        return response.data.data;
    },
    verifyResetOtp: async ({ challengeToken, otp }: { challengeToken: string; otp: string }): Promise<ResetAuthorization> => {
        const response = await axiosInstance.post<ApiResponse<ResetAuthorization>>(`Account/VerifyResetOtp`, { resetChallengeToken: challengeToken, otp });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'invalid or expired code');
        }
        return response.data.data;
    },
    resetPassword: async (
        emailOrArgs: string | { authorizationToken: string; newPassword: string },
        token?: string,
        newPassword?: string,
        isFirstLogin?: boolean,
        userId?: string
    ): Promise<ApiResponse<boolean>> => {
        if (typeof emailOrArgs === 'object') {
            const response = await axiosInstance.post<ApiResponse<boolean>>(`Account/ResetPassword`, {
                authorizationToken: emailOrArgs.authorizationToken,
                newPassword: emailOrArgs.newPassword,
            });
            if (!response.data.succeeded) {
                throw new Error(response.data.messages?.[0] || 'failed to reset password');
            }
            return response.data;
        }
        const response = await axiosInstance.post<ApiResponse<boolean>>(`Account/ResetPassword`, {
            email: emailOrArgs, token, newPassword, isFirstLogin, userId,
        });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to reset password');
        }
        return response.data;
    },
    checkDuplicate: async (email?: string, username?: string): Promise<ApiResponse<boolean>> => {
        const response = await axiosInstance.post<ApiResponse<boolean>>(`v1/Users/check-duplicate`, { email, username });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to check duplicate');
        }
        return response.data;
    },
    updateEmployeeProfile: async (id: number, data: EmployeeProfileUpdate): Promise<ApiResponse<number>> => {
        const response = await axiosInstance.patch<ApiResponse<number>>(`v1/Employees/${id}/profile`, data);
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'Failed to update profile');
        }
        return response.data;
    },
    updateStudentProfile: async (id: number, data: StudentProfileUpdate): Promise<ApiResponse<number>> => {
        const response = await axiosInstance.patch<ApiResponse<number>>(`v1/Students/${id}/profile`, data);
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'Failed to update profile');
        }
        return response.data;
    },
    confirmEmail: async (userId:string, token: string): Promise<ApiResponse<boolean>> => {
        const response = await axiosInstance.get<ApiResponse<boolean>>(`Account/ConfirmEmail`, {params: {userId, token }});
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to confirm email');
        }
        return response.data;
    },
    resendVerificationEmail: async (userId: string): Promise<ApiResponse<boolean>> => {
        const response = await axiosInstance.post<ApiResponse<boolean>>(`Account/ResendEmailConfirmation`, { userId });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to resend verification email');
        }
        return response.data;
    },
}